import React, { useState, useEffect } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import MiniAgentForm from "../../components/MiniAgent/MiniAgentForm";
import { API_URL } from "../../constant";

const BecomeAgent = () => {
  const navigate = useNavigate();
  const { userData } = useOutletContext();
  const [showForm, setShowForm] = useState(true);
  const [hasExistingApplication, setHasExistingApplication] = useState(false);
  const [applicationStatus, setApplicationStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!userData) {
      navigate("/sign_in");
      return;
    }

    // Check if user is already an agent
    const isAdvertisingAgent =
      userData?.role?.includes("Advertising Agent") ||
      userData?.isAdvertisingAgent ||
      userData?.role === "mini" ||
      userData?.role === "senior";

    if (isAdvertisingAgent) {
      navigate("/customer/wallet");
      return;
    }

    checkExistingApplication();
  }, [userData, navigate]);

  const checkExistingApplication = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      if (!token) return;

      const response = await fetch(
        `${API_URL}/api/advertising-agents/application`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.application) {
          setHasExistingApplication(true);
          setApplicationStatus(data.application.status);
        }
      }
    } catch (error) {
      console.error("Error checking application:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMiniAgentSubmit = (responseData) => {
    console.log("Mini-Agent Form submitted successfully:", responseData);
    setShowForm(false);
    setHasExistingApplication(true);
    setApplicationStatus("pending");
  };

  const handleCloseForm = () => {
    navigate("/customer/wallet");
  };

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div
          style={{
            width: "40px",
            height: "40px",
            border: "4px solid #f3f3f3",
            borderTop: "4px solid #3498db",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        ></div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Show application status if already applied
  if (hasExistingApplication) {
    return (
      <div
        style={{
          padding: "40px 20px",
          maxWidth: "800px",
          margin: "0 auto",
        }}
      >
        <div
          style={{
            background: "#fff",
            borderRadius: "20px",
            padding: "40px",
            boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
            textAlign: "center",
          }}
        >
          <h2 style={{ color: "#333", marginBottom: "20px" }}>
            Application Status
          </h2>
          
          {applicationStatus === "pending" && (
            <>
              <div
                style={{
                  width: "80px",
                  height: "80px",
                  border: "6px solid #f3f3f3",
                  borderTop: "6px solid #3498db",
                  borderRadius: "50%",
                  margin: "0 auto 30px",
                  animation: "spin 1s linear infinite",
                }}
              ></div>
              <h3 style={{ color: "#3498db", marginBottom: "15px" }}>
                Application Under Review
              </h3>
              <p style={{ color: "#666", marginBottom: "30px" }}>
                Your application to become a Mini Advertising Agent is being reviewed.
                We'll notify you once a decision is made.
              </p>
            </>
          )}

          {applicationStatus === "approved" && (
            <>
              <div
                style={{
                  width: "80px",
                  height: "80px",
                  background: "#10b981",
                  borderRadius: "50%",
                  margin: "0 auto 30px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  fontSize: "40px",
                }}
              >
                ✓
              </div>
              <h3 style={{ color: "#10b981", marginBottom: "15px" }}>
                Application Approved!
              </h3>
              <p style={{ color: "#666", marginBottom: "30px" }}>
                Congratulations! You are now a Mini Advertising Agent.
                You can now access agent features and start referring customers.
              </p>
              <button
                onClick={() => navigate("/customer/wallet")}
                style={{
                  background: "#10b981",
                  color: "white",
                  border: "none",
                  padding: "12px 30px",
                  borderRadius: "25px",
                  cursor: "pointer",
                  fontSize: "16px",
                }}
              >
                Go to Dashboard
              </button>
            </>
          )}

          {applicationStatus === "rejected" && (
            <>
              <div
                style={{
                  width: "80px",
                  height: "80px",
                  background: "#ef4444",
                  borderRadius: "50%",
                  margin: "0 auto 30px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  fontSize: "40px",
                }}
              >
                ✗
              </div>
              <h3 style={{ color: "#ef4444", marginBottom: "15px" }}>
                Application Not Approved
              </h3>
              <p style={{ color: "#666", marginBottom: "15px" }}>
                We're sorry, but your application was not approved at this time.
              </p>
              <p style={{ color: "#666", marginBottom: "30px", fontSize: "14px" }}>
                Please contact support for more information.
              </p>
              <button
                onClick={() => navigate("/customer/wallet")}
                style={{
                  background: "#6b7280",
                  color: "white",
                  border: "none",
                  padding: "12px 30px",
                  borderRadius: "25px",
                  cursor: "pointer",
                  fontSize: "16px",
                }}
              >
                Go Back
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // Show the MiniAgentForm if no existing application
  return (
    <div
      style={{
        padding: "20px",
        maxWidth: "900px",
        margin: "0 auto",
      }}
    >
      {showForm ? (
        <div
          style={{
            background: "#fff",
            borderRadius: "20px",
            padding: "20px",
            boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
            
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginBottom: "30px",
              textAlign:"center",
            }}
          >

          </div>
          <MiniAgentForm
            onSubmit={handleMiniAgentSubmit}
            onClose={handleCloseForm}
          />
        </div>
      ) : (
        <div
          style={{
            background: "#fff",
            borderRadius: "20px",
            padding: "40px",
            boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
            textAlign: "center",
          }}
        >
          <div
            style={{
              width: "80px",
              height: "80px",
              border: "6px solid #f3f3f3",
              borderTop: "6px solid #3498db",
              borderRadius: "50%",
              margin: "0 auto 30px",
              animation: "spin 1s linear infinite",
            }}
          ></div>
          <h3 style={{ color: "#3498db", marginBottom: "15px" }}>
            Application Submitted
          </h3>
          <p style={{ color: "#666", marginBottom: "30px" }}>
            Your application to become a Mini Advertising Agent has been submitted successfully.
            We'll notify you once it's reviewed.
          </p>
          <button
            onClick={handleCloseForm}
            style={{
              background: "#3498db",
              color: "white",
              border: "none",
              padding: "12px 30px",
              borderRadius: "25px",
              cursor: "pointer",
              fontSize: "16px",
            }}
          >
            Go to Dashboard
          </button>
        </div>
      )}
    </div>
  );
};

export default BecomeAgent;